import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./LeagueList.module.css";
import Subscription from "../Subscription/Subscription";
import CompetitionsAPI from "../../data/competitions";
import { GlobalContext } from "../../constants/Context";
import { useLeagueListState } from "../../states/useLeagueListState";
import CustomDropdown from "../../components/Input/CustomDropdown";
import { MdSportsCricket } from "react-icons/md";
const LeagueList = () => {
  const { SITE_ID, CLUB_ID } = useContext(GlobalContext)
  // State for Play-Cricket API data

  const {
    playCricketCompetitions,
    setPlayCricketCompetitions,
    playCricketLoading,
    setPlayCricketLoading,
    playCricketError,
    setPlayCricketError,
    leagueData,
    setLeagueData,
    filteredLeagues,
    setFilteredLeagues,
    loading,
    setLoading,
    selectedYear,
    setSelectedYear,
    searchTerm,
    setSearchTerm,
    loadedYears,
    setLoadedYears,
    yearsList,
    setYearsList,
  } = useLeagueListState();

  // Generate years list (from 2000 to current year + 5)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2000;
    const endYear = currentYear + 5;

    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push({
        value: year.toString(),
        label: year.toString(),
      });
    }
    setYearsList(years);
  }, []);

  // Function to transform Play-Cricket competitions to league format
  const transformPlayCricketToLeagues = useCallback((competitions, year) => {
    if (!competitions || competitions.length === 0) return [];

    // Use a Set to track unique competition IDs
    const uniqueCompetitions = [];
    const seenIds = new Set();

    competitions.forEach((comp) => {
      // Check for duplicate IDs
      if (!seenIds.has(comp.id)) {
        seenIds.add(comp.id);
        uniqueCompetitions.push({
          id: comp.id,
          name: comp.name,
          competition_id: comp.id,
          sport_type: "cricket",
          season: year,
          is_active: true,
          play_cricket_id: comp.id,
          league_id: SITE_ID,
          year: year,
          // Add a unique key for React
          uniqueKey: `${comp.id}-${year}`
        });
      }
    });

    return uniqueCompetitions;
  }, []);

  // Handle Play-Cricket API data
  const handlePlayCricketData = useCallback(
    (data) => {
      // Check if we've already loaded data for this year
      if (loadedYears.has(selectedYear)) {
        setPlayCricketLoading(false);
        return;
      }

      const transformedLeagues = transformPlayCricketToLeagues(
        data,
        selectedYear
      );

      // Store the raw competitions for reference
      setPlayCricketCompetitions(transformedLeagues);

      // Update league data - REPLACE instead of merge
      setLeagueData((prev) => {
        // Remove all competitions from this year
        const filteredPrev = prev.filter(league => league.year !== selectedYear);

        // Add new transformed competitions
        const updatedData = [...filteredPrev, ...transformedLeagues];

        return updatedData;
      });

      // Mark this year as loaded
      setLoadedYears(prev => new Set([...prev, selectedYear]));

      setPlayCricketLoading(false);
    },
    [selectedYear, transformPlayCricketToLeagues, loadedYears]
  );

  const handlePlayCricketError = useCallback((error) => {
    setPlayCricketError(error);
    setPlayCricketLoading(false);
    console.error("Play-Cricket API Error:", error);
  }, []);

  const handlePlayCricketLoading = useCallback((isLoading) => {
    setPlayCricketLoading(isLoading);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Set initial data
    setLeagueData([]);
    setFilteredLeagues([]);
    setLoadedYears(new Set()); // Reset loaded years
    setLoading(false);
  }, []);

  // Filter leagues based on search term and selected year
  useEffect(() => {
    let filtered = leagueData;

    // Filter by selected year
    filtered = filtered.filter((league) => league.year === selectedYear);

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((league) =>
        league.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Remove duplicates based on unique key
    const uniqueFiltered = [];
    const seenKeys = new Set();

    filtered.forEach(league => {
      const key = league.uniqueKey || `${league.id}-${league.year}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueFiltered.push(league);
      }
    });
    setFilteredLeagues(uniqueFiltered);
  }, [leagueData, selectedYear, searchTerm]);

  const yearOptions = useMemo(() =>
    yearsList?.map(year => ({
      value: year.value,
      label: year.label
    })) || [], [yearsList]
  );

  // Handle year change
  const handleYearChange = (year) => {
    if (!year || year === selectedYear) return;

    setSelectedYear(year);
    setPlayCricketLoading(true);

    // Clear search
    setSearchTerm("");

    // Clear error
    setPlayCricketError(null);
  };

  // Clear search input
  const clearSearch = () => {
    setSearchTerm("");
  };



  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show loading only when API is loading for the selected year
  const isLoading =
    playCricketLoading &&
    !loadedYears.has(selectedYear);

  return (
    <div className={styles.leagueListPage}>
      {/* API Component - Re-fetches when selectedYear changes */}
      <CompetitionsAPI
        key={selectedYear}
        leagueId={SITE_ID}
        season={selectedYear}
        onDataLoaded={handlePlayCricketData}
        onError={handlePlayCricketError}
        onLoading={handlePlayCricketLoading}
      />

      {/* Header Section */}
      <section className={styles.headerSection}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <h1 className={styles.heroTitle}>
              <span>Cricket Competitions</span>
            </h1>
            <p className={styles.pageSubtitle}>
              Discover and follow cricket competitions
            </p>

          </div>
        </div>
      </section>

      {/* Search and Year Filter Section */}
      <section className={styles.controlsSection}>
        <div className={styles.container}>
          <div className={styles.controlsRow}>
            {/* Year Select Dropdown - Moved to left side */}
            <div className={styles.filterGroup}>
              <div className={styles.seasonFilter}>
                <label className={styles.filterLabel}>
                  <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none">
                    <path d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                      stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                  </svg>
                  Season
                </label>
                <div className={styles.selectWrapper}>
                  <CustomDropdown
                    options={yearOptions}
                    value={selectedYear}
                    onChange={handleYearChange} placeholder="Select Season"
                  />
                </div>
              </div>
            </div>

            {/* Search Box - Moved to right side */}
            <div className={styles.searchGroup}>
              <div className={styles.searchBox}>
                <div className={styles.searchIcon}>
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search competitions by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                  aria-label="Search competitions"
                />
                {searchTerm && (
                  <button
                    className={styles.searchClear}
                    onClick={clearSearch}
                    aria-label="Clear search"
                    type="button"
                  >
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitions Grid */}
      <section className={styles.leaguesSection}>
        <div className={styles.container} style={{
          borderRadius: "8px"
        }}>
          <h2 className={styles.sectionTitle}>Competition - {selectedYear}</h2>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Loading competitions for {selectedYear}...</p>
            </div>
          ) : filteredLeagues.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><MdSportsCricket/></div>
              <h3>No Competitions Found</h3>
              <p>
                {searchTerm
                  ? `No competitions found matching "${searchTerm}" for ${selectedYear}`
                  : `No competitions available for ${selectedYear}`}
              </p>

            </div>
          ) : (
            <>
              <div className={styles.divisionsTitle}>
                <h3 className={styles.h3}>DIVISIONS</h3>
                <div className={styles.resultsCount}>
                  Showing {filteredLeagues.length} competition{filteredLeagues.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div className={styles.leaguesGrid}>
                {filteredLeagues.map((league, index) => (
                  <div
                    key={league.uniqueKey || `${league.id}-${league.year}`}
                    className={styles.leagueCard}
                    style={{ "--card-index": index }}
                  >
                    {/* Card Header with Sport Icon */}
                    <div className={styles.cardHeader}>
                      <Link
                        to={`/league-table/${league.play_cricket_id}`}
                        className={`${styles.primary}`}
                        state={{ year: selectedYear }}
                      >
                        <h3 className={styles.leagueName}>{league.name}</h3>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <Subscription />
    </div>
  );
};

export default LeagueList;